import { useEffect, useState } from "react";
import { endpoints, formatApiError, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, UserPlus, Trash2, Save } from "lucide-react";
import SsoSettings from "@/components/SsoSettings";

const inputCls = "bg-white border-zinc-200 rounded-sm h-9 font-mono text-[12px] focus-visible:ring-orange-500/40";

export default function Workspace() {
  const { user, workspace, refreshWorkspace } = useAuth();
  const [members, setMembers] = useState([]);
  const [name, setName] = useState(workspace?.name || "");
  const [member, setMember] = useState({ email: "", name: "", password: "", role: "operator" });
  const isOwner = user?.role === "owner";

  const load = () => endpoints.workspace().then((r) => setMembers(r.members));
  useEffect(() => { load(); }, []);

  const rename = async () => {
    try {
      const ws = await endpoints.renameWorkspace(name);
      refreshWorkspace(ws);
      toast.success("Workspace renamed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await endpoints.addMember(member);
      toast.success("Member added", { description: member.email });
      setMember({ email: "", name: "", password: "", role: "operator" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await endpoints.removeMember(id);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-[1100px] px-12 py-14" data-testid="page-workspace">
      <div className="tx-overline mb-2">Tenant</div>
      <h1 className="font-heading font-black text-4xl mb-2">Workspace</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Everything in Private Offer Autopilot — rate cards, runs, authorizations, CPPOs, integrations —
        is isolated to this ISV workspace.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-0 border border-zinc-200">
        <div className="p-6 border-b lg:border-b-0 lg:border-r border-zinc-200">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-orange-600" />
            <span className="tx-overline">Identity</span>
          </div>
          <div className="space-y-3">
            <div>
              <div className="tx-overline mb-1.5">Name</div>
              <div className="flex gap-2">
                <Input data-testid="workspace-name-input" value={name} onChange={(e) => setName(e.target.value)}
                       disabled={!isOwner} className={inputCls} />
                <Button onClick={rename} disabled={!isOwner || name.trim().length < 2} data-testid="workspace-rename-button"
                        className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm h-9 gap-2">
                  <Save className="w-3.5 h-3.5" /> Save
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <div className="tx-overline mb-1">Slug</div>
                <div className="font-mono text-[12px] text-zinc-700" data-testid="workspace-slug">{workspace?.slug}</div>
              </div>
              <div>
                <div className="tx-overline mb-1">Workspace ID</div>
                <div className="font-mono text-[12px] text-zinc-700" data-testid="workspace-id">{workspace?.id}</div>
              </div>
              <div>
                <div className="tx-overline mb-1">Created</div>
                <div className="font-mono text-[12px] text-zinc-700">{fmtTime(workspace?.created_at)}</div>
              </div>
              <div>
                <div className="tx-overline mb-1">Your role</div>
                <div className="font-mono text-[12px] text-emerald-600 uppercase">{user?.role}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-orange-600" />
            <span className="tx-overline">Add member</span>
          </div>
          {isOwner ? (
            <form onSubmit={addMember} className="space-y-2" data-testid="add-member-form">
              <Input data-testid="member-name-input" placeholder="Full name" required value={member.name}
                     onChange={(e) => setMember({ ...member, name: e.target.value })} className={inputCls} />
              <Input data-testid="member-email-input" type="email" placeholder="email@isv.com" required value={member.email}
                     onChange={(e) => setMember({ ...member, email: e.target.value })} className={inputCls} />
              <Input data-testid="member-password-input" type="password" placeholder="Temporary password (8+)" minLength={8} required
                     value={member.password} onChange={(e) => setMember({ ...member, password: e.target.value })} className={inputCls} />
              <div className="flex gap-2">
                <select data-testid="member-role-select" value={member.role} onChange={(e) => setMember({ ...member, role: e.target.value })}
                        className="flex-1 h-9 bg-white border border-zinc-200 rounded-sm px-2 text-[12px] font-mono text-zinc-800">
                  <option value="operator">operator</option>
                  <option value="owner">owner</option>
                </select>
                <Button type="submit" data-testid="add-member-button" className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm h-9">
                  Add
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-[12px] text-zinc-500">Only workspace owners can add members.</p>
          )}
        </div>
      </div>

      <div className="mt-10"><SsoSettings /></div>

      <div className="tx-overline mt-10 mb-3">Members · {members.length}</div>
      <div className="border border-zinc-200 divide-y divide-zinc-200" data-testid="members-list">
        {members.map((m) => (
          <div key={m.id} data-testid="member-row" className="px-5 py-3 flex items-center justify-between">
            <div>
              <div className="text-[13px] text-zinc-900">{m.name} {m.id === user?.id && <span className="text-zinc-500">(you)</span>}</div>
              <div className="font-mono text-[11px] text-zinc-500">{m.email}</div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`text-[10px] font-mono uppercase ${m.role === "owner" ? "text-orange-600" : "text-zinc-600"}`}>{m.role}</span>
              {isOwner && m.id !== user?.id && (
                <button data-testid="remove-member-button" onClick={() => remove(m.id)}
                        className="text-zinc-400 hover:text-rose-600 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
